<template>
  <div class="my-details">
    <div class="header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <div class="title">结算信息 <i @click="openInstructions" class="el-icon-info"
            style="margin-left:4px; color: #fff;"></i> </div>
      </div>
    </div>

    <div class="card">
      <!-- 筛选条件 -->
      <div class="filter-section" v-if="isAdmin">
        <div class="filter-row">
          <div class="filter-item">
            <label>推广位ID：</label>
            <el-select v-model="settlementFilters.positionId" @change="onSettlementFilterChange" placeholder="全部"
              clearable>
              <el-option label="全部" value="" />
              <el-option v-for="position in positionOptions" :key="position.positionId"
                :label="`${position.positionId} ${position.positionName ? `(${position.positionName})` : '(---)'}`"
                :value="position.positionId" />
            </el-select>
          </div>
          <div class="filter-item">
            <label>结算状态：</label>
            <el-select v-model="settlementFilters.status" @change="onSettlementFilterChange" placeholder="全部" clearable>
              <el-option label="全部" value="" />
              <el-option label="待结算" value="PENDING" />
              <el-option label="已结算" value="PAID" />
            </el-select>
          </div>
        </div>
      </div>
      <!-- 管理员视图：结算账单列表 -->
      <div v-if="isAdmin" class="settlement-section">
        <div class="section-header">
          <div class="actions">
            <button class="btn-secondary" @click="exportSettlements">导出</button>
            <button v-if="isAdminUser" class="btn-secondary" @click="openSummaryModal">汇总</button>
            <el-date-picker v-if="isAdminUser" v-model="selectedMonth" type="month" placeholder="选择月份" format="yyyy-MM"
              value-format="yyyy-MM" @change="handleMonthChange" prefix-icon="" style="width: 100px;">
            </el-date-picker>
            <button class="btn-primary" @click="refreshSettlements">加载</button>
          </div>
        </div>

        <div v-if="settlements.length === 0" class="empty">暂无结算账单</div>
        <div v-else class="table-container">
          <!-- 选择统计信息 -->
          <div v-if="selectedSettlements.length > 0" class="selection-summary">
            <div class="summary-info">
              <span class="total-amount">已选{{ selectedSettlements.length }}个</span>
              <span class="total-amount">待结算:¥{{ selectedPendingAmount }}</span>
              <span class="total-amount">已结算:¥{{ selectedConfirmedAmount }}</span>
            </div>
          </div>
          <div v-else class="selection-summary">
            <div class="summary-info">
              <span class="total-amount">
                <i class="el-icon-info" style="margin-left:4px; color: #909399; cursor:pointer;"></i>
                勾选可显示本月待结算/已结算的总金额。
              </span>
            </div>
          </div>
          <!-- 使用Element UI的树形表格 -->
          <el-table ref="settlementTable" :data="settlements" style="width: 100%" row-key="id" border
            :tree-props="{ children: 'children' }" :default-expand-all="false" @row-click="handleRowClick"
            class="settlement-tree-table" :max-height="tableHeight" :fixed-header="true"
            @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="40"></el-table-column>
            <el-table-column prop="positionId" label="ID" width="100">
              <template slot-scope="scope">
                <!-- <span v-if="scope.row.isLeader" class="leader-indicator">👑</span> -->
                {{ scope.row.positionId }}
              </template>
            </el-table-column>
            <el-table-column prop="positionName" label="名称" min-width="100">
              <template slot-scope="scope">
                <span v-if="scope.row.isLeader" class="leader-name">{{ scope.row.positionName }} (团长)</span>
                <span v-else-if="scope.row.isAdmin" class="admin-name">{{ scope.row.positionName }}</span>
                <span v-else-if="scope.row.isLeaderPosition" class="leader-name">{{ scope.row.positionName }}</span>
                <span v-else>{{ scope.row.positionName }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="settlementAmount" label="本月" width="100">
              <template slot-scope="scope">
                <span class="amount">¥{{ scope.row.settlementAmount }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="nextMonthEstimate" label="下月" width="100">
              <template slot-scope="scope">
                <span class="amount">¥{{ scope.row.nextMonthEstimate || '0.00' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <span class="status" :class="scope.row.status">{{ getSettlementStatusText(scope.row.status) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" min-width="120" align="center" fixed="right">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <button v-if="scope.row.status === 'PENDING'" class="btn-confirm"
                    @click.stop="confirmSettlement(scope.row.id)" style="width: 100%;">
                    结算
                  </button>
                  <button class="btn-history" @click.stop="showUserWithdrawHistory(scope.row)" title="查看提现历史">
                    历史
                  </button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 普通用户视图：个人结算账单 -->
      <div v-else class="user-settlement">
        <div class="section-header">
          <div class="actions">
            <span v-if="lastRefreshTime" class="last-refresh">
              最后更新：{{ formatTime(lastRefreshTime) }}
            </span>
            <button class="btn-primary" @click="refreshUserSettlements" :disabled="loading">
              {{ loading ? '刷新中...' : '刷新数据' }}
            </button>
          </div>
        </div>
        <div class="summary">
          <div class="summary-card">
            <div class="summary-label">本月预估结算佣金</div>
            <div class="summary-amount">
              ¥{{ settlementsSummary.currentMonthEstimate }}
              <span v-if="settlementsSummary.currentMonthStatus" class="settlement-status">{{
                settlementsSummary.currentMonthStatus }}</span>
            </div>
            <div class="summary-desc">即上个月确认收货的订单</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">下月预估结算佣金</div>
            <div class="summary-amount">¥{{ settlementsSummary.nextMonthEstimate }}</div>
            <div class="summary-desc">本月确认收货的订单</div>
          </div>
        </div>
        <!-- 提现历史列表 -->
        <div v-if="userSettlements.length === 0" class="empty">暂无提现记录</div>
        <div v-else class="table-container">
          <div class="table-scroll-container">
            <el-table :data="userSettlements" class="withdraw-table" style="width: 100%">
              <el-table-column prop="period" label="月份" width="70"></el-table-column>
              <el-table-column prop="totalOrders" label="单数" width="60">
                <template slot-scope="scope">
                  {{ scope.row.totalOrders || '--' }}
                </template>
              </el-table-column>
              <el-table-column prop="totalActualFee" label="提成" width="100">
                <template slot-scope="scope">
                  <span class="amount">¥{{ scope.row.totalActualFee }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="amount" label="佣金" width="100">
                <template slot-scope="scope">
                  <span class="amount">¥{{ scope.row.amount }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="createdAt" label="结算时间" width="85"></el-table-column>
              <el-table-column prop="settlementAccount" label="账号" width="120">
                <template slot-scope="scope">
                  {{ getSettlementMethodText(scope.row.settlementMethod) + '-' + scope.row.settlementAccount.slice(-4)
                    ||
                    '--' }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>

    </div>

    <!-- 确认结算弹窗 -->
    <div v-if="showSettlementModal" class="modal-overlay" @click="closeSettlementModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>确认结算</h3>
          <button class="modal-close" @click="closeSettlementModal">×</button>
        </div>
        <div class="modal-body">
          <div class="settlement-info">
            <div class="info-row">
              <span class="label">用户名：</span>
              <span class="value">{{ selectedSettlement.username }}</span>
            </div>
            <div class="info-row">
              <span class="label">手机号：</span>
              <span class="value">{{ selectedSettlement.phone }}</span>
            </div>
            <div class="info-row">
              <span class="label">推广ID：</span>
              <span class="value">{{ selectedSettlement.positionId }}</span>
            </div>
            <div class="info-row">
              <span class="label">推广名称：</span>
              <span class="value">{{ selectedSettlement.positionName }}</span>
            </div>
            <div class="info-row">
              <span class="label">结算单数：</span>
              <span class="value">{{ selectedSettlement.totalOrders || 0 }} 单</span>
            </div>
            <div class="info-row">
              <span class="label">结算预估金额：</span>
              <span class="value amount">¥{{ selectedSettlement.settlementAmount }}</span>
            </div>
            <div class="info-row">
              <span class="label">结算实际金额：</span>
              <span class="value amount">¥{{ calculateEstimatedAmount(selectedSettlement.settlementAmount) || '0.00'
              }}</span>
            </div>
            <!-- 结算方式选择 -->
            <div class="info-row">
              <span class="label">结算方式：</span>
              <div class="payment-method">
                <el-radio-group v-model="selectedPaymentMethod" size="small">
                  <el-radio label="bank">银行卡</el-radio>
                  <el-radio label="alipay">支付宝</el-radio>
                  <el-radio label="wechat">微信</el-radio>
                </el-radio-group>
              </div>
            </div>
            <!-- 银行卡信息 -->
            <div v-if="selectedPaymentMethod === 'bank'" class="payment-info">
              <div class="info-row">
                <span class="label">银行卡号：</span>
                <div class="value-with-copy">
                  <span class="value">{{ selectedSettlement.bankCard || '--' }}</span>
                  <button v-if="selectedSettlement.bankCard && selectedSettlement.bankCard !== '--'" class="copy-btn"
                    @click="copyBankCard" title="复制银行卡号">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                        fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="info-row">
                <span class="label">银行名称：</span>
                <span class="value">{{ selectedSettlement.bankName || '--' }}</span>
              </div>
            </div>
            <!-- 支付宝信息 -->
            <div v-if="selectedPaymentMethod === 'alipay'" class="payment-info">
              <div class="info-row">
                <span class="label">支付宝账号：</span>
                <div class="value-with-copy">
                  <span class="value">{{ selectedSettlement.alipayAccount || '--' }}</span>
                  <button v-if="selectedSettlement.alipayAccount && selectedSettlement.alipayAccount !== '--'"
                    class="copy-btn" @click="copyAlipayAccount" title="复制支付宝账号">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                        fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="info-row">
                <span class="label">预留手机号：</span>
                <span class="value">{{ selectedSettlement.alipayPhone || '--' }}</span>
              </div>
            </div>
            <!-- 微信信息 -->
            <div v-if="selectedPaymentMethod === 'wechat'" class="payment-info">
              <div class="info-row">
                <span class="label">微信账号：</span>
                <div class="value-with-copy">
                  <span class="value">{{ selectedSettlement.wechatAccount || '--' }}</span>
                  <button v-if="selectedSettlement.wechatAccount && selectedSettlement.wechatAccount !== '--'"
                    class="copy-btn" @click="copyWechatAccount" title="复制微信账号">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                        fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="info-row">
                <span class="label">预留手机号：</span>
                <span class="value">{{ selectedSettlement.wechatPhone || '--' }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeSettlementModal">取消</button>
          <button class="btn-confirm" @click="confirmSettlementAction">确认结算</button>
        </div>
      </div>
    </div>

    <!-- 实名认证弹窗 -->
    <div v-if="showVerificationModal" class="modal-overlay" @click="closeVerificationModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>实名认证提醒</h3>
          <button class="modal-close" @click="closeVerificationModal">×</button>
        </div>
        <div class="modal-body">
          <div class="verification-icon">🔒</div>
          <p class="verification-message">
            您还未完成实名认证，无法查看结算账单。
          </p>
          <p class="verification-desc">
            请先完成实名认证，以便正常使用相关功能。
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeVerificationModal">稍后再说</button>
          <button class="btn-confirm" @click="goToVerification">立即认证</button>
        </div>
      </div>
    </div>

    <!-- 汇总统计弹窗 -->
    <div v-if="showSummaryModal" class="modal-overlay" @click="closeSummaryModal">
      <div class="modal-content summary-modal" @click.stop>
        <div class="modal-header">
          <h3>本月结算汇总统计</h3>
          <button class="modal-close" @click="closeSummaryModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="summaryLoading" class="loading">正在加载汇总数据...</div>
          <div v-else class="summary-content">
            <!-- Admin统计 -->
            <div class="summary-group">
              <h4 class="group-title">管理员 (Admin)</h4>
              <div class="summary-row">
                <span class="amount pending">待结算：¥{{ summaryData.admin.pending }}</span>
                <span class="amount pendingDif" v-if="summaryData.admin.settlementAmountDif">
                  DIF: ¥{{ summaryData.admin.settlementAmountDif }}
                </span>
                <span>base: ¥{{ summaryData.admin.pending - summaryData.admin.settlementAmountDif }}</span>
                <span class="amount confirmed">已结算：¥{{ summaryData.admin.confirmed }}</span>
              </div>
            </div>

            <!-- 团长统计 -->
            <div v-for="leader in summaryData.leaders" :key="leader.positionId" class="summary-group">
              <h4 class="group-title">{{ leader.positionName }} (团长)</h4>
              <div class="summary-row">
                <span class="amount pending">待结算：¥{{ leader.pending }}</span>
                <span class="amount pendingDif" v-if="leader.settlementAmountDif">
                  DIF: ¥{{ leader.settlementAmountDif }}
                </span>
                <span>base: ¥{{ leader.pending - leader.settlementAmountDif }}</span>
                <span class="amount confirmed">已结算：¥{{ leader.confirmed }}</span>
              </div>
              <div class="sub-info">包含自身及 {{ leader.childCount }} 个子推广位</div>
            </div>

            <!-- 其他推广位统计 -->
            <div class="summary-group">
              <h4 class="group-title">其他推广位</h4>
              <div class="summary-row">
                <span class="amount pending">待结算：¥{{ summaryData.others.pending }}</span>
                <span class="amount pendingDif" v-if="summaryData.others.settlementAmountDif">
                  DIF: ¥{{ summaryData.others.settlementAmountDif }}
                </span>
                <span>base: ¥{{ summaryData.others.pending - summaryData.others.settlementAmountDif }}</span>
                <span class="amount confirmed">已结算：¥{{ summaryData.others.confirmed }}</span>
              </div>
              <div class="sub-info">共 {{ summaryData.others.count }} 个推广位</div>
            </div>
            <!-- 总计 -->
            <div class="summary-group">
              <h4 class="group-title">总计</h4>
              <div class="summary-row">
                <span class="amount pending">待结算：¥{{ summaryData.total.pending }}</span>
                <span class="amount pendingDif" v-if="summaryData.total.settlementAmountDif">
                  DIF: ¥{{ summaryData.total.settlementAmountDif }}
                </span>
                <span>base: ¥{{ summaryData.total.pending - summaryData.total.settlementAmountDif }}</span>
                <span class="amount confirmed">已结算：¥{{ summaryData.total.confirmed }}</span>
              </div>
              <div class="sub-info">共 {{ summaryData.total.count }} 个推广位</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" style="width: 100%;" @click="closeSummaryModal">关闭</button>
        </div>
      </div>
    </div>

    <!-- 提现历史弹窗 -->
    <div v-if="showWithdrawModal" class="modal-overlay" @click="closeWithdrawModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>提现历史 - {{ selectedUserWithdraw.positionId }}</h3>
          <button class="modal-close" @click="closeWithdrawModal">×</button>
        </div>
        <div class="modal-body">
          <div class="modal-user-info">
            <div class="info-row">
              <span class="label">推广位：</span>
              <span class="value">{{ selectedUserWithdraw.positionName }}</span>
            </div>
            <div class="info-row">
              <span class="label">用户名：</span>
              <span class="value">{{ selectedUserWithdraw.username }}</span>
            </div>
            <div class="info-row">
              <span class="label">手机号：</span>
              <span class="value">{{ selectedUserWithdraw.phone }}</span>
            </div>
          </div>

          <div class="history-section">
            <h4>历史结算记录</h4>
            <div v-if="userWithdrawHistory.length === 0" class="empty">暂无历史记录</div>
            <div v-else class="history-table">
              <el-table :data="userWithdrawHistory" style="width: 100%">
                <el-table-column prop="month" label="结算月份" width="80"></el-table-column>
                <el-table-column prop="totalOrders" label="总单数" width="80"></el-table-column>
                <el-table-column prop="totalCommission" label="总佣金" width="100">
                  <template slot-scope="scope">
                    <span class="amount">¥{{ scope.row.totalCommission }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="totalCommission" label="实际佣金" width="100">
                  <template slot-scope="scope">
                    <span class="amount">¥{{ scope.row.totalActualFee }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="settlementMethod" label="结算方式" width="80">
                  <template slot-scope="scope">
                    {{ getSettlementMethodText(scope.row.settlementMethod) }}
                  </template>
                </el-table-column>
                <el-table-column prop="settlementAccount" label="结算账号" min-width="180">
                  <template slot-scope="scope">
                    {{ scope.row.settlementAccount || '--' }}
                  </template>
                </el-table-column>
                <el-table-column prop="settlementTime" label="结算时间" width="120"></el-table-column>
              </el-table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" style="width: 100%;" @click="closeWithdrawModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMySettlements, getAdminSettlements, getAllPositions, confirmSettlement, getRealNameStatus, exportSettlementsToExcel } from '@/utils/jdApi'
export default {
  name: 'MyDetails',
  data() {
    return {
      settlements: [],
      userSettlements: [],
      settlementsSummary: {},
      loading: false,
      isAdmin: false,
      lastRefreshTime: null,
      // 筛选条件
      settlementFilters: {
        positionId: '',
        status: ''
      },
      positionOptions: [], // 推广位选项
      showVerificationModal: false, // 实名认证弹窗
      showSettlementModal: false, // 确认结算弹窗
      selectedSettlement: {}, // 选中的结算记录
      selectedPaymentMethod: 'bank', // 选中的结算方式
      showWithdrawModal: false, // 提现历史弹窗
      selectedUserWithdraw: {}, // 选中的用户提现历史
      userWithdrawHistory: [], // 用户提现历史数据
      selectedSettlements: [], // 选中的结算记录
      selectedPendingAmount: '0.00', // 选中记录中待结算的总金额
      selectedConfirmedAmount: '0.00', // 选中记录中已结算的总金额
      tableHeight: 0,
      showSummaryModal: false, // 汇总统计弹窗
      summaryLoading: false, // 汇总数据加载状态
      summaryData: { // 汇总统计数据
        admin: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00' },
        leaders: [],
        others: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00', count: 0 },
        total: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00', count: 0 }
      },
      selectedMonth: null, // 选中的月份（初始化为当前月份）
      currentQueryMonth: null // 当前查询的月份（用于导出）
    }
  },
  computed: {
    // 当前月份显示（格式：yyyy-MM）
    currentMonthDisplay() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    // 判断是否为admin用户（不包括团长）
    isAdminUser() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          return user.role === 'ADMIN'
        }
        return false
      } catch (error) {
        console.error('检查用户角色失败:', error)
        return false
      }
    }
  },
  activated() {
    this.checkUserRole()
    this.loadData()
    this.tableHeight = window.innerHeight - 310
  },
  methods: {
    openInstructions() {
      const instructions = `
        一、已完成实名认证的用户，将在每月25日前收到上个月（自然月）已完成订单的结算账单，并于25日进行打款。
        如未及时完成实名认证，会导致不能结算或者延后结算。<br/>
        二、个人账户的资金将自动转入绑定的银行卡、支付宝、微信等账户中，佣金将在扣除京东接口数据服务费(20%)平台缴纳所得税(6%)以及提现手续费(4%)后进行发放。<br/>
        三、用户需正确填写姓名、银行卡、支付宝、微信等相关信息，否则将影响结算账单和佣金发放，上月已完成订单的订单佣金将在每月25日自动到账。<br/>
        四、如发生恶意刷单、欺诈等非法活动，需退回对应佣金。如账户余额不足以支付退回订单的佣金，平台有权回收佣金。<br/>
        五、如订单发生退货或换货，上月佣金将被扣除。如订单处于售后状态（如换货、申请价保等），完成时间及对应的可提现时间将重新计算。`
      // 使用 MessageBox 显示提现说明
      this.$msgbox({
        title: '提现说明',
        message: instructions,
        confirmButtonText: '我知道了',
        confirmButtonClass: 'confirm-dialog-button',
        showCancelButton: false,
        dangerouslyUseHTMLString: true,
        customClass: 'withdrawal-instructions-messagebox',
        beforeClose: (action, instance, done) => {
          done()
        }
      }).catch(() => {
        // 用户点击关闭或取消
      })
    },
    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },
    // 检查用户角色
    checkUserRole() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          // 管理员和团长都使用管理界面样式
          this.isAdmin = user.role === 'ADMIN' || user.role === 'LEADER'
        }
      } catch (error) {
        console.error('检查用户角色失败:', error)
        this.isAdmin = false
      }
    },

    loadData() {
      if (this.isAdmin) {
        // 初始化月份选择器为当前月份
        if (!this.selectedMonth) {
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          this.selectedMonth = `${year}-${month}`
        }
        this.loadSettlements()
        this.loadPositionOptions()
      } else {
        // 普通用户先检查实名认证状态
        this.checkVerificationStatus()
      }
    },

    // 加载结算账单数据（管理员）
    loadSettlements(month = null) {
      this.loading = true
      // 如果没有指定月份，清除保存的月份（使用当前月份）
      if (!month) {
        this.currentQueryMonth = null
      }

      const params = {
        page: 0,
        size: 500,
        ...this.settlementFilters
      }

      // 如果指定了月份，添加到参数中
      if (month) {
        params.month = month
      } else if (this.currentQueryMonth) {
        // 如果有保存的月份，使用保存的月份
        params.month = this.currentQueryMonth
      }

      getAdminSettlements(params).then(res => {
        if (res && res.success) {
          const data = res.data || {}
          this.settlements = data.items || []
        } else {
          this.settlements = []
          this.$message.error('加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },


    // 加载普通用户结算数据（接口）
    loadUserSettlements() {
      this.loading = true
      getMySettlements()
        .then(res => {
          if (res && res.success) {
            const data = res.data || {}
            this.userSettlements = data.items || []
            this.settlementsSummary = data.summary || {}
          } else {
            this.$message.error('加载结算数据失败')
          }
        })
        .catch(error => {
          console.error('加载结算数据失败:', error)
          this.$message.error('加载结算数据失败')
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 检查实名认证状态（普通用户）
    checkVerificationStatus() {
      getRealNameStatus()
        .then(res => {
          if (res && res.success) {
            const data = res.data
            if (!data || !data.verificationStatus || data.verificationStatus !== 'VERIFIED') {
              // 未实名认证，显示弹窗
              this.showVerificationModal = true
            } else {
              // 已实名认证，正常加载数据
              this.loadUserSettlements()
            }
          } else {
            // 获取状态失败，显示弹窗
            this.showVerificationModal = true
          }
        })
        .catch(error => {
          console.error('检查实名认证状态失败:', error)
          // 出错时也显示弹窗
          this.showVerificationModal = true
        })
    },

    // 跳转到实名认证页面
    goToVerification() {
      this.showVerificationModal = false
      this.$router.push('/realname-verification')
    },

    // 关闭实名认证弹窗，返回到我的主页面
    closeVerificationModal() {
      this.showVerificationModal = false
      // 返回到我的主页面
      this.$router.push('/mypage')
    },


    // 刷新结算数据
    refreshSettlements() {
      // 清除月份筛选，重新加载当前月份的数据
      this.currentQueryMonth = null
      // 重置月份选择器为当前月份
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      this.selectedMonth = `${year}-${month}`
      this.loadSettlements()
      this.lastRefreshTime = new Date()
      this.$message.success('结算数据已刷新')
    },


    // 刷新普通用户结算数据
    refreshUserSettlements() {
      this.loadUserSettlements()
      this.lastRefreshTime = new Date()
      this.$message.success('结算数据已刷新')
    },


    // 格式化时间
    formatTime(date) {
      if (!date) return ''
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)

      if (minutes < 1) return '刚刚'
      if (minutes < 60) return `${minutes}分钟前`

      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}小时前`

      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 确认结算
    confirmSettlement(id) {
      const item = this.settlements.find(s => s.id === id)
      if (item) {
        this.selectedSettlement = item
        this.showSettlementModal = true
      }
    },

    // 计算预估金额（实际金额的70%）
    calculateEstimatedAmount(actualAmount) {
      if (!actualAmount) return '0.00'
      const amount = parseFloat(actualAmount) * 0.7
      return amount.toFixed(2)
    },

    // 确认结算操作
    confirmSettlementAction() {
      // 校验结算方式和账号
      if (!this.validateSettlementInfo()) {
        return
      }

      const id = this.selectedSettlement.id
      const totalActualFee = this.calculateEstimatedAmount(this.selectedSettlement.settlementAmount)
      const requestData = {
        settlementMethod: this.selectedPaymentMethod,
        settlementAccount: this.getSettlementAccount(),
        totalActualFee: totalActualFee
      }

      // 如果有选择的月份，添加到请求参数中（非必传）
      if (this.currentQueryMonth) {
        requestData.month = this.currentQueryMonth
      }

      confirmSettlement(id, requestData).then(res => {
        if (res && res.success) {
          const item = this.settlements.find(s => s.id === id)
          if (item) {
            item.status = 'CONFIRMED'
          }
          this.$message.success('结算已确认')
          this.closeSettlementModal()
        } else {
          this.$message.error('确认结算失败')
        }
      }).catch(error => {
        console.error('确认结算失败:', error)
        this.$message.error('确认结算失败')
      })
    },

    // 校验结算信息
    validateSettlementInfo() {
      if (this.selectedPaymentMethod === 'bank') {
        if (!this.selectedSettlement.bankCard || this.selectedSettlement.bankCard === '--') {
          this.$message.error('请先设置银行卡号')
          return false
        }
      } else if (this.selectedPaymentMethod === 'alipay') {
        if (!this.selectedSettlement.alipayAccount || this.selectedSettlement.alipayAccount === '--') {
          this.$message.error('请先设置支付宝账号')
          return false
        }
      } else if (this.selectedPaymentMethod === 'wechat') {
        if (!this.selectedSettlement.wechatAccount || this.selectedSettlement.wechatAccount === '--') {
          this.$message.error('请先设置微信账号')
          return false
        }
      }
      return true
    },

    // 获取结算账号
    getSettlementAccount() {
      if (this.selectedPaymentMethod === 'bank') {
        return this.selectedSettlement.bankCard || '--'
      } else if (this.selectedPaymentMethod === 'alipay') {
        return this.selectedSettlement.alipayAccount || '--'
      } else if (this.selectedPaymentMethod === 'wechat') {
        return this.selectedSettlement.wechatAccount || '--'
      }
      return '--'
    },

    // 关闭确认结算弹窗
    closeSettlementModal() {
      this.showSettlementModal = false
      this.selectedSettlement = {}
      this.selectedPaymentMethod = 'bank' // 重置为默认选择银行卡
    },

    // 处理表格行点击事件
    handleRowClick(row) {
      // 所有行都可以点击查看提现历史（包括团长）
      this.showUserWithdrawHistory(row)
    },


    // 显示用户提现历史弹窗
    showUserWithdrawHistory(item) {
      this.selectedUserWithdraw = item
      this.showWithdrawModal = true

      this.getUserWithdrawHistory(item.positionId)
        .then(res => {
          if (res && res.success) {
            this.userWithdrawHistory = res.data.history || []
          } else {
            this.userWithdrawHistory = []
            this.$message.error('获取提现历史失败')
          }
        })
        .catch(error => {
          console.error('获取提现历史失败:', error)
          this.userWithdrawHistory = []
          this.$message.error('获取提现历史失败')
        })
    },

    // 获取结算方式的中文文本
    getSettlementMethodText(method) {
      const methodMap = {
        'bank': '银行卡',
        'alipay': '支付宝',
        'wechat': '微信'
      }
      return methodMap[method] || '银行卡'
    },

    // 关闭提现历史弹窗
    closeWithdrawModal() {
      this.showWithdrawModal = false
      this.selectedUserWithdraw = {}
      this.userWithdrawHistory = []
    },

    // 显示汇总统计弹窗
    openSummaryModal() {
      this.showSummaryModal = true
      this.loadSummaryData()
    },

    // 关闭汇总统计弹窗
    closeSummaryModal() {
      this.showSummaryModal = false
      this.summaryData = {
        admin: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00' },
        leaders: [],
        others: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00', count: 0 },
        total: { pending: '0.00', settlementAmountDif: null, confirmed: '0.00', count: 0 }
      }
    },

    // 处理月份选择变化
    handleMonthChange(value) {
      if (!value) {
        // 如果清空选择，恢复到当前月份
        this.currentQueryMonth = null
        this.loadSettlements()
        return
      }

      // 保存当前查询的月份（用于导出）
      this.currentQueryMonth = value

      // 调用接口查询指定月份的数据
      this.loading = true

      getAdminSettlements({
        page: 0,
        size: 500,
        month: value,
        ...this.settlementFilters
      }).then(res => {
        if (res && res.success) {
          const data = res.data || {}
          this.settlements = data.items || []
          this.$message.success(`已加载 ${value} 月份的结算数据`)
        } else {
          this.settlements = []
          this.$message.error('加载失败')
        }
      }).catch(error => {
        console.error('加载月份数据失败:', error)
        this.$message.error('加载月份数据失败')
      }).finally(() => {
        this.loading = false
      })
    },

    // 加载汇总数据
    loadSummaryData() {
      this.summaryLoading = true

      // 计算汇总数据
      this.calculateSummaryData()

      this.summaryLoading = false
    },

    // 计算汇总数据
    calculateSummaryData() {
      const admin = { pending: 0, settlementAmountDif: 0, confirmed: 0 }
      const leaders = []
      const others = { pending: 0, settlementAmountDif: 0, confirmed: 0, count: 0 }
      const total = { pending: 0, settlementAmountDif: 0, confirmed: 0 }

      // 遍历所有结算数据
      this.settlements.forEach(item => {
        if (item.isAdmin) {
          // 管理员数据
          const amount = parseFloat(item.settlementAmount) || 0
          const difAmount = parseFloat(item.settlementAmountDif) || 0
          if (item.status === 'PENDING') {
            admin.pending += amount
          } else if (item.status === 'CONFIRMED') {
            admin.confirmed += amount
          }
          // 累加 settlementAmountDif（如果有）
          admin.settlementAmountDif += difAmount
        } else if (item.isLeader) {
          // 团长数据（包含自身和子节点）
          const leaderData = this.calculateLeaderSummary(item)
          leaders.push(leaderData)
        } else {
          // 其他推广位数据
          const amount = parseFloat(item.settlementAmount) || 0
          const difAmount = parseFloat(item.settlementAmountDif) || 0
          others.count++
          if (item.status === 'PENDING') {
            others.pending += amount
          } else if (item.status === 'CONFIRMED') {
            others.confirmed += amount
          }
          // 累加 settlementAmountDif（如果有）
          others.settlementAmountDif += difAmount
        }
      })

      // 计算总计
      total.pending = admin.pending + leaders.reduce((sum, leader) => sum + leader.pending, 0) + others.pending
      total.settlementAmountDif = admin.settlementAmountDif + leaders.reduce((sum, leader) => sum + (leader.settlementAmountDif || 0), 0) + others.settlementAmountDif
      total.confirmed = admin.confirmed + leaders.reduce((sum, leader) => sum + leader.confirmed, 0) + others.confirmed

      // 计算推广位总数
      // 管理员：1个 + 团长数量 + 其他推广位数量
      const adminCount = admin.pending > 0 || admin.confirmed > 0 ? 1 : 0
      const leaderCount = leaders.length
      const totalCount = adminCount + leaderCount + others.count

      // 更新数据
      this.summaryData = {
        admin: {
          pending: admin.pending.toFixed(2),
          settlementAmountDif: admin.settlementAmountDif > 0 ? admin.settlementAmountDif.toFixed(2) : null,
          confirmed: admin.confirmed.toFixed(2)
        },
        leaders: leaders.map(leader => ({
          ...leader,
          pending: leader.pending.toFixed(2),
          settlementAmountDif: leader.settlementAmountDif > 0 ? leader.settlementAmountDif.toFixed(2) : null,
          confirmed: leader.confirmed.toFixed(2)
        })),
        others: {
          pending: others.pending.toFixed(2),
          settlementAmountDif: others.settlementAmountDif > 0 ? others.settlementAmountDif.toFixed(2) : null,
          confirmed: others.confirmed.toFixed(2),
          count: others.count
        },
        total: {
          pending: total.pending.toFixed(2),
          settlementAmountDif: total.settlementAmountDif > 0 ? total.settlementAmountDif.toFixed(2) : null,
          confirmed: total.confirmed.toFixed(2),
          count: totalCount
        }
      }
    },

    // 计算团长汇总数据（包含自身和子节点）
    calculateLeaderSummary(leaderItem) {
      let pending = 0
      let settlementAmountDif = 0
      let confirmed = 0
      let childCount = 0

      // 团长自身
      const leaderAmount = parseFloat(leaderItem.settlementAmount) || 0
      const leaderDifAmount = parseFloat(leaderItem.settlementAmountDif) || 0
      if (leaderItem.status === 'PENDING') {
        pending += leaderAmount
      } else if (leaderItem.status === 'CONFIRMED') {
        confirmed += leaderAmount
      }
      // 累加 settlementAmountDif（如果有）
      settlementAmountDif += leaderDifAmount

      // 团长的子节点
      if (leaderItem.children && leaderItem.children.length > 0) {
        childCount = leaderItem.children.length
        leaderItem.children.forEach(child => {
          const childAmount = parseFloat(child.settlementAmount) || 0
          const childDifAmount = parseFloat(child.settlementAmountDif) || 0
          if (child.status === 'PENDING') {
            pending += childAmount
          } else if (child.status === 'CONFIRMED') {
            confirmed += childAmount
          }
          // 累加 settlementAmountDif（如果有）
          settlementAmountDif += childDifAmount
        })
      }

      return {
        positionId: leaderItem.positionId,
        positionName: leaderItem.positionName,
        pending: pending,
        settlementAmountDif: settlementAmountDif,
        confirmed: confirmed,
        childCount: childCount
      }
    },
    // 处理选择变化
    handleSelectionChange(selection) {
      this.selectedSettlements = selection
      this.calculateSelectedTotal()
    },

    // 计算选中记录的总金额
    calculateSelectedTotal() {
      let pendingTotal = 0
      let confirmedTotal = 0

      this.selectedSettlements.forEach(item => {
        const amount = parseFloat(item.settlementAmount) || 0
        if (item.status === 'PENDING') {
          pendingTotal += amount
        } else if (item.status === 'CONFIRMED') {
          confirmedTotal += amount
        }
      })

      this.selectedPendingAmount = pendingTotal.toFixed(2)
      this.selectedConfirmedAmount = confirmedTotal.toFixed(2)
    },
    // 获取用户提现历史
    getUserWithdrawHistory(positionId) {
      return fetch(`/api/admin/withdraws/${positionId}`, {
        method: 'GET',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      })
        .then(response => response.json())
        .catch(error => {
          console.error('获取提现历史失败:', error)
          throw error
        })
    },

    // 复制银行卡号
    copyBankCard() {
      const bankCard = this.selectedSettlement.bankCard
      if (!bankCard || bankCard === '--') {
        this.$message.warning('银行卡号为空，无法复制')
        return
      }

      navigator.clipboard.writeText(bankCard).then(() => {
        this.$message.success('银行卡号已复制到剪贴板')
      }).catch(() => {
        // 降级处理
        const textArea = document.createElement('textarea')
        textArea.value = bankCard
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$message.success('银行卡号已复制到剪贴板')
      })
    },

    // 复制支付宝账号
    copyAlipayAccount() {
      const alipayAccount = this.selectedSettlement.alipayAccount
      if (!alipayAccount || alipayAccount === '--') {
        this.$message.warning('支付宝账号为空，无法复制')
        return
      }

      navigator.clipboard.writeText(alipayAccount).then(() => {
        this.$message.success('支付宝账号已复制到剪贴板')
      }).catch(() => {
        // 降级处理
        const textArea = document.createElement('textarea')
        textArea.value = alipayAccount
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$message.success('支付宝账号已复制到剪贴板')
      })
    },

    // 复制微信账号
    copyWechatAccount() {
      const wechatAccount = this.selectedSettlement.wechatAccount
      if (!wechatAccount || wechatAccount === '--') {
        this.$message.warning('微信账号为空，无法复制')
        return
      }

      navigator.clipboard.writeText(wechatAccount).then(() => {
        this.$message.success('微信账号已复制到剪贴板')
      }).catch(() => {
        // 降级处理
        const textArea = document.createElement('textarea')
        textArea.value = wechatAccount
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$message.success('微信账号已复制到剪贴板')
      })
    },

    // 导出Excel
    async exportSettlements() {
      try {
        this.$message.info('正在导出Excel文件...')
        // 构建导出参数（使用当前筛选条件）
        const exportParams = {
          positionId: this.settlementFilters.positionId || undefined,
          status: this.settlementFilters.status || undefined,
          month: this.currentQueryMonth || undefined // 如果有查询的月份，也用于导出
        }

        // 调用导出API
        const blob = await exportSettlementsToExcel(exportParams)

        // 创建下载链接
        const url = window.URL.createObjectURL(new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))

        // 生成文件名
        const now = new Date()
        const dateStr = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0')
        // 如果查询了特定月份，使用该月份；否则使用当前月份
        const monthStr = this.currentQueryMonth || (now.getFullYear() + '-' +
          String(now.getMonth() + 1).padStart(2, '0'))
        const fileName = `结算账单_${monthStr}_${dateStr}.xlsx`

        // 创建临时链接并触发下载
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()

        // 清理
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$message.success('Excel文件导出成功')
      } catch (error) {
        console.error('导出Excel失败:', error)
        this.$message.error('导出Excel失败')
      }
    },

    // 获取结算状态文本
    getSettlementStatusText(status) {
      const map = {
        'PENDING': '待结',
        'CONFIRMED': '已结'
      }
      return map[status] || '未知'
    },

    // 筛选相关方法
    onSettlementFilterChange() {
      this.loadSettlements()
    },

    // 加载推广位选项列表（管理员）
    loadPositionOptions() {
      getAllPositions()
        .then(res => {
          if (res && res.success && res.data) {
            this.positionOptions = res.data
            // 为了保持兼容性，同时更新旧格式的positionIdOptions
            this.positionIdOptions = res.data.map(item => item.positionId)
          } else {
            // 降级到旧API
            this.loadPositionIdsFallback()
          }
        })
        .catch(() => {
          // 降级到旧API
          this.loadPositionIdsFallback()
        })
    },


  }
}
</script>

<style scoped lang="scss">
.my-details {
  margin: 0 auto;
  padding: 20px;
}

.header {
  background: linear-gradient(135deg, #42b983, #369870);
  padding: 4px;
  border-radius: 12px;
  margin-bottom: 10px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: white;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.back-btn svg {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.tab {
  background: white;
  color: #42b983;
  border: 1px solid #42b983;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.tab.active {
  background: #42b983;
  color: white;
}

.tab:hover:not(.active) {
  background: #f0f9f5;
}

.card {
  background: white;
  border: 1px solid #eaecef;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.last-refresh {
  font-size: 12px;
  color: #999;
  margin-right: 10px;
}

.btn-primary,
.btn-secondary {
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  flex: 1;
}

.btn-primary {
  background: #42b983;
  color: white;
}

.btn-primary:hover {
  background: #369870;
}

.btn-secondary {
  background: #fff;
  border: 1px solid #42b983;
  color: #42b983;
  padding: 4px 12px;
}

.table-container {
  overflow-x: auto;
}

.table-scroll-container {
  overflow-x: auto;
  width: 100%;
}

.selection-summary {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.total-amount {
  font-size: 14px;
  color: #42b983;
}

.settlement-table,
.withdraw-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  /* min-width: 600px; */
  /* 设置最小宽度确保表格内容完整显示 */
}

.settlement-table th,
.withdraw-table th {
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.settlement-table td,
.withdraw-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.settlement-table tr:hover,
.withdraw-table tr:hover {
  background: #f8f9fa;
}

.amount {
  font-weight: 600;
  color: #42b983;
  font-size: 15px;
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status.PENDING {
  background: #fff3cd;
  color: #856404;
}

.status.CONFIRMED {
  background: #d1ecf1;
  color: #42b983;
}

.status.PAID {
  background: #d4edda;
  color: #155724;
}

.status.FAILED {
  background: #f8d7da;
  color: #721c24;
}

.action-buttons {
  display: flex;
  gap: 6px;
}

.btn-confirm,
.btn-paid,
.btn-detail {
  padding: 4px 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-confirm {
  background: #42b983;
  color: white;
}

.btn-confirm:hover {
  background: #42b983;
}

.btn-paid {
  background: #28a745;
  color: white;
}

.btn-paid:hover {
  background: #218838;
}

.btn-detail {
  background: #f5f5f5;
  color: white;
}

.btn-detail:hover {
  background: #5a6268;
}

.btn-disabled {
  background: #e9ecef !important;
  color: #f5f5f5 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}

.empty::before {
  content: "📋";
  display: block;
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

/* 普通用户视图样式 */
.user-settlement,
.user-withdraw,
.user-dashboard {
  padding: 0;
}

/* 用户仪表板样式 */
.user-dashboard {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.summary-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.summary-section h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
}

.summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.summary-item {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  text-align: center;
}

.summary-item .label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.summary-item .value {
  font-size: 20px;
  font-weight: 600;
  color: #42b983;
}

.withdraw-section {
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-card {
  border: 1px solid #e8f5ee;
  background: #f6fffb;
  border-radius: 8px;
  padding: 12px;
}

.summary-label {
  font-size: 12px;
  color: #666;
}

.summary-amount {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 700;
  color: #42b983;
}

.settlement-status {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #67C23A;
  background: #F0F9FF;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #B3E19D;
}

.summary-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.item {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
}

.item:hover {
  border-color: #42b983;
  box-shadow: 0 2px 4px rgba(66, 185, 131, 0.1);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.left .primary {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
  font-weight: 500;
}

.left .secondary {
  font-size: 12px;
  color: #888;
}

.right {
  text-align: right;
}

.right .amount {
  font-size: 16px;
  font-weight: 600;
  color: #42b983;
}

.right .status {
  font-size: 12px;
  margin-top: 6px;
  padding: 2px 6px;
  border-radius: 3px;
}

.right .status.pending {
  background: #fff3cd;
  color: #856404;
}

.right .status.done {
  background: #d4edda;
  color: #155724;
}

.right .status.failed {
  background: #f8d7da;
  color: #721c24;
}

/* 筛选控件样式 */
.filter-section {
  margin-bottom: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.filter-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.filter-item input,
.filter-item select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 120px;
}

.filter-item input:focus,
.filter-item select:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.1);
}

.btn-clear {
  padding: 6px 12px;
  background: #f5f5f5;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear:hover {
  background: #5a6268;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f5f5f5;
  color: #666;
}

.modal-body {
  // padding: 0px 10px;
  text-align: center;
}

.verification-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.verification-message {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.verification-desc {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 10px;
  justify-content: center;
}

.modal-footer .btn-cancel,
.modal-footer .btn-confirm {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 100px;
}

.modal-footer .btn-cancel {
  border: 1px solid #42b983;
  color: #42b983;
  background: none;
}


.modal-footer .btn-confirm {
  background: #42b983;
  color: white;
}

.modal-footer .btn-confirm:hover {
  background: #369870;
}

/* 确认结算弹窗特有样式 */
.settlement-info {
  text-align: left;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.info-row .label {
  font-weight: 600;
  color: #333;
  min-width: 80px;
  margin-right: 12px;
}

.info-row .value {
  color: #666;
  flex: 1;
}

.info-row .value.amount {
  color: #42b983;
  font-weight: 600;
  font-size: 16px;
}

.value-with-copy {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.copy-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn:hover {
  background: #f0f0f0;
  color: #42b983;
}

.copy-btn svg {
  width: 16px;
  height: 16px;
}

/* 结算方式选择样式 */
.payment-method {
  flex: 1;

  .el-radio-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .el-radio {
    margin-right: 0px;
  }
}

.payment-info {
  margin-top: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #42b983;
}

/* 提现历史弹窗样式 */
.history-section {
  margin-top: 20px;
}

.history-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
}

.history-table {
  max-height: 300px;
  overflow-y: auto;
}

.history-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.history-table th,
.history-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.history-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.history-table tr:hover {
  background: #f8f9fa;
}

/* 可点击行样式 */
.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background: #f0f8ff !important;
}

/* 树形表格样式 */
.settlement-tree-table {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.settlement-tree-table .el-table__row {
  cursor: pointer;
}

.settlement-tree-table .el-table__row:hover {
  background: #f0f8ff !important;
}

/* 团长标识样式 */
.leader-indicator {
  margin-right: 4px;
  font-size: 14px;
}

.leader-name {
  font-weight: 600;
  color: #42b983;
}

.admin-name {
  font-weight: 600;
  color: #42b983;
}

/* 历史按钮样式 */
.btn-history {
  background-color: #fff;
  border: 1px solid #42b983;
  color: #42b983;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  width: 100%;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .my-details {
    padding: 10px;
    background-color: #fff;
  }

  .card {
    padding: 10px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .actions {
    width: 100%;
    justify-content: space-between;
  }

  .settlement-table,
  .withdraw-table {
    font-size: 12px;
  }

  .settlement-table th,
  .withdraw-table th,
  .settlement-table td,
  .withdraw-table td {
    padding: 8px 4px;
  }

  .btn-confirm,
  .btn-paid,
  .btn-detail {
    width: 100%;
    text-align: center;
  }

  .summary {
    grid-template-columns: 1fr;
  }

  .user-settlement .summary,
  .user-withdraw .summary,
  .user-dashboard .summary {
    grid-template-columns: 1fr;
  }

  .summary-section {
    padding: 15px;
  }

  .summary-item {
    padding: 12px;
  }

  .summary-item .value {
    font-size: 18px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filter-item {
    justify-content: space-between;
  }

  .filter-item input,
  .filter-item select {
    min-width: 150px;
  }
}

@media (max-width: 480px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    padding: 4px;
  }

  .tabs {
    width: 100%;
    justify-content: center;
    margin-bottom: 10px;
  }

  .tab {
    flex: 1;
    text-align: center;
  }
}

/* 汇总统计弹窗样式 */
.summary-modal {
  width: 700px;
  max-width: 95vw;
}

.summary-content {
  max-height: 500px;
  overflow-y: auto;
}

.summary-group {
  margin-bottom: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.group-title {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 8px;
  justify-content: space-between;
}

.summary-row .label {
  font-size: 14px;
  color: #666;
  min-width: 60px;
}

.summary-row .amount {
  font-size: 16px;
  font-weight: 600;
  min-width: 80px;
}

.summary-row .amount.pending {
  color: #E6A23C;
}

.summary-row .amount.pendingDif {
  color: #F56C6C;
  font-weight: 600;
}

.summary-row .amount.confirmed {
  color: #67C23A;
}

.sub-info {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}



.total-title {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 700;
  color: #42b983;
  text-align: center;
}

::v-deep {
  .el-input--suffix .el-input__inner {
    padding-right: 10px;
  }

  .actions {
    .el-input__inner {
      border: 1px solid #42b983 !important;
    }
  }


  .withdrawal-instructions-messagebox {
    width: 600px;

  }

  .confirm-dialog-button {
    background-color: #42b983 !important;
  }

  .withdrawal-instructions-messagebox .el-message-box__message {
    text-align: left !important;
    white-space: pre-line !important;
    /* 保留换行和空格 */
    word-wrap: break-word !important;
    /* 自动换行 */
    line-height: 1.8 !important;
    font-size: 14px !important;
    color: #606266 !important;
    max-height: 60vh;
    overflow-y: auto;
    padding: 20px 0 !important;
  }

  .withdrawal-instructions-messagebox .el-message-box__content {
    padding: 20px 30px !important;
  }

}
</style>

<style>
/* 全局样式 - 用于Element UI组件 */
.settlement-tree-table .el-table__expand-icon {
  width: 7px !important;
}

.settlement-tree-table .el-table__cell {
  padding: 8px 0px !important;
}

/* 固定表头样式 */
.settlement-tree-table .el-table__header-wrapper {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* 固定右侧列样式 */
.settlement-tree-table .el-table__fixed-right {
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

/* 固定列背景色 */
.settlement-tree-table .el-table__fixed-right .el-table__cell {
  background-color: #fff;
}

/* 表头固定时的背景色 */
.settlement-tree-table .el-table__header-wrapper .el-table__cell {
  background-color: #f8f9fa;
}
</style>
